import { Controller } from "@hotwired/stimulus"
import { motionMs } from "../lib/motion"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

// Bottom Sheet Controller
// iOS-like bottom sheet with drag-to-dismiss functionality
//
// Features:
// - Show/hide with slide animation
// - Backdrop click to close
// - Swipe down to dismiss (touch/mouse)
// - Body scroll lock when open
// - Motion-token (--motion-base) animations
export default class extends Controller {
  static targets = ["backdrop", "sheet"]
  static values = {
    closeOnBackdrop: { type: Boolean, default: true }
  }

  connect() {
    this.touchStartY = 0
    this.currentY = 0
    this.isDragging = false

    // Listen for custom dialog events (category-picker integration)
    this.boundHandleDialogOpen = this.handleDialogOpen.bind(this)
    this.boundHandleDialogClose = this.handleDialogClose.bind(this)
    window.addEventListener('dialog:open', this.boundHandleDialogOpen)
    window.addEventListener('dialog:close', this.boundHandleDialogClose)

    // Ensure bottom navigation is restored before Turbo caches the page
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener('turbo:before-cache', this.boundBeforeCache)
  }

  disconnect() {
    this.cleanup()

    // Remove event listeners to prevent memory leaks
    window.removeEventListener('dialog:open', this.boundHandleDialogOpen)
    window.removeEventListener('dialog:close', this.boundHandleDialogClose)
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache)
  }

  // Snapshot the sheet closed (not removed): a back/forward restore renders the cache without refetching, so the
  // dialog must still be in it.
  beforeCache() {
    this.sheetTarget.classList.remove("translate-y-0", "scale-100", "opacity-100")
    this.sheetTarget.classList.add("translate-y-full", "scale-95", "opacity-0")
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0", "hidden")
    this.element.classList.add("pointer-events-none")
    this.cleanup()
  }

  // Handle dialog:open event (dispatched by category-picker)
  handleDialogOpen(event) {
    const dialogId = event.detail?.dialogId
    if (!dialogId) return

    // Check if this bottom-sheet instance should respond to the event
    // Expected element ID format: "{dialogId}-backdrop"
    if (this.element.id === `${dialogId}-backdrop`) {
      this.open()
    }
  }

  // Handle dialog:close event (dispatched by category-picker)
  handleDialogClose(event) {
    const dialogId = event.detail?.dialogId
    if (!dialogId) return

    // Check if this bottom-sheet instance should respond to the event
    if (this.element.id === `${dialogId}-backdrop`) {
      this.close()
    }
  }

  // Open bottom sheet
  open() {
    // Allow pointer interactions
    this.element.classList.remove("pointer-events-none")

    // Show backdrop
    this.backdropTarget.classList.remove("hidden")

    // Force reflow for animation
    this.backdropTarget.offsetHeight

    // Fade in backdrop
    this.backdropTarget.classList.remove("opacity-0")
    this.backdropTarget.classList.add("opacity-100")

    // Slide up sheet
    requestAnimationFrame(() => {
      this.sheetTarget.classList.remove("translate-y-full", "scale-95", "opacity-0")
      this.sheetTarget.classList.add("translate-y-0", "scale-100", "opacity-100")
    })

    // Hide bottom navigation bar
    this.hideBottomNavigation()

    // Lock body scroll
    this.lock()

    // Focus first focusable element (accessibility)
    // Exclude input/textarea to prevent mobile keyboard from appearing on open
    // Desktop/screen reader users can still Tab to inputs manually
    requestAnimationFrame(() => {
      const focusable = this.sheetTarget.querySelector(
        'button:not([disabled]), [href], select, [tabindex]:not([tabindex="-1"]):not(input):not(textarea)'
      )
      if (focusable) {
        focusable.focus()
      }
    })

    this.dispatch("opened", { detail: { id: this.element.id } })
  }

  // Close bottom sheet
  close() {
    const animationDuration = motionMs("BASE_MS")

    // Slide down sheet
    this.sheetTarget.classList.remove("translate-y-0", "scale-100", "opacity-100")
    this.sheetTarget.classList.add("translate-y-full", "scale-95", "opacity-0")

    // Fade out backdrop
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0")

    // Note: showBottomNavigation() is called in cleanup() to avoid duplicate calls
    // that can cause timing issues with navigation transform state

    // Wait for animation, then cleanup (keep DOM for reuse)
    setTimeout(() => {
      this.cleanup()
      // DOM is kept in the page for reuse - category picker targets remain accessible
      this.backdropTarget.classList.add("hidden")
      this.element.classList.add("pointer-events-none")
    }, animationDuration)
  }

  // Cleanup resources
  cleanup() {
    this.unlock()  // Restore body scroll
    this.showBottomNavigation()  // Restore bottom nav
  }

  // Close on backdrop click (only if closeOnBackdrop is true)
  closeOnBackdrop(event) {
    // Only close if clicked directly on backdrop, not bubbled from sheet
    if (event.target === this.backdropTarget && this.closeOnBackdropValue) {
      this.close()
    }
  }

  // Stop event propagation (prevent internal clicks from closing dialog)
  stop(event) {
    event.stopPropagation()
  }

  // Touch drag handlers (iOS-like swipe down to dismiss)
  handleTouchStart(event) {
    this.touchStartY = event.touches ? event.touches[0].clientY : event.clientY
    this.isDragging = true
    this.sheetTarget.style.transition = "none"
  }

  handleTouchMove(event) {
    if (!this.isDragging) return

    this.currentY = event.touches ? event.touches[0].clientY : event.clientY
    const deltaY = this.currentY - this.touchStartY

    // Only allow downward drag
    if (deltaY > 0) {
      this.sheetTarget.style.transform = `translateY(${deltaY}px)`
    }
  }

  handleTouchEnd(event) {
    if (!this.isDragging) return

    this.isDragging = false
    const deltaY = this.currentY - this.touchStartY

    // Re-enable transition
    this.sheetTarget.style.transition = ""

    // Close if dragged down beyond threshold
    if (deltaY > window.BusinessConstants.UITiming.DRAG_DISMISS_THRESHOLD_PX) {
      this.close()
    } else {
      // Snap back to original position
      this.sheetTarget.style.transform = ""
    }
  }

  // Page scroll lock (shared, counted: a sheet over another overlay doesn't unlock it on close)
  lock() {
    lockScroll(this, { touch: true, dismiss: () => this.close() })
  }

  unlock() {
    unlockScroll(this)
  }

  // Hide bottom navigation bar
  hideBottomNavigation() {
    // Find all fixed bottom navigation bars (robust selector)
    // Handles both floating-tabbar controller and plain fixed nav elements
    // Updated to catch ALL fixed bottom elements (div, nav, etc.) for ad wizard compatibility
    const navs = document.querySelectorAll('.fixed.bottom-0, nav.fixed')

    let hiddenCount = 0
    navs.forEach(nav => {
      // Verify element is actually at bottom (position: fixed + bottom: 0)
      const computedStyle = window.getComputedStyle(nav)
      const isBottomFixed = computedStyle.position === 'fixed' &&
                           (nav.classList.contains('bottom-0') || computedStyle.bottom === '0px')

      if (isBottomFixed) {
        // 플래그 설정: keyboard_cover_controller가 이 요소를 건드리지 않도록
        // 바텀시트가 열린 동안 keyboard 이벤트로 transform이 덮어쓰이는 것 방지
        nav.dataset.bottomSheetControlled = 'true'
        nav.style.transform = 'translateY(100%)'
        nav.style.transition = `transform ${window.BusinessConstants.UITiming.ANIMATION_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
        hiddenCount++
      }
    })

    if (hiddenCount === 0) {
      console.warn('[BottomSheet] No bottom navigation found to hide')
    }
  }

  // Show bottom navigation bar
  // Only restores elements this controller actually hid (flag-gated via data-bottom-sheet-controlled)
  // Uses empty string (not translateY(0)) to fully remove inline style, preventing
  // conflicts with CSS class-based transforms (FAB hover, keyboard_cover_controller, etc.)
  showBottomNavigation() {
    const navs = document.querySelectorAll('[data-bottom-sheet-controlled]')

    navs.forEach(nav => {
      delete nav.dataset.bottomSheetControlled
      nav.style.transform = ''
      nav.style.transition = ''
    })
  }
}
